import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, light, ...props }) => (
  <label className={`block border-b py-4 focus-within:border-[#C67D57] transition-colors ${light ? "border-black/15" : "border-white/15"}`}>
    <span className={`font-sans uppercase tracking-[0.2em] text-[10px] block mb-2 ${light ? "text-[#6B6560]" : "text-[#F2EFEB]/70"}`}>{label}</span>
    <input
      {...props}
      className={`w-full bg-transparent text-lg outline-none placeholder:text-[#8C8681]/40 ${light ? "text-[#1A1715]" : "text-[#F2EFEB]"}`}
    />
  </label>
);

export const ContactForm = ({ initialMessage = "", onSubmitted, light = false }) => {
  const [form, setForm] = useState({ name: "", email: "", message: initialMessage });
  const [loading, setLoading] = useState(false);
  const { t } = useLang();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("form.fillAll"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      const name = form.name;
      setForm({ name: "", email: "", message: "" });
      onSubmitted && onSubmitted();
      navigate("/tesekkurler", { state: { name } });
    } catch (err) {
      toast.error(t("form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-2">
      <Field
        light={light}
        label={t("form.name")}
        data-testid="contact-name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder={t("form.namePh")}
      />
      <Field
        light={light}
        label={t("form.email")}
        type="email"
        data-testid="contact-email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder={t("form.emailPh")}
      />
      <label className={`block border-b py-4 focus-within:border-[#C67D57] transition-colors ${light ? "border-black/15" : "border-white/15"}`}>
        <span className={`font-sans uppercase tracking-[0.2em] text-[10px] block mb-2 ${light ? "text-[#6B6560]" : "text-[#F2EFEB]/70"}`}>{t("form.message")}</span>
        <textarea
          data-testid="contact-message"
          rows={3}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder={t("form.messagePh")}
          className={`w-full bg-transparent text-lg outline-none resize-none placeholder:text-[#8C8681]/40 ${light ? "text-[#1A1715]" : "text-[#F2EFEB]"}`}
        />
      </label>
      <button
        data-testid="contact-submit"
        type="submit"
        disabled={loading}
        className={`group mt-4 flex items-center gap-3 font-sans uppercase tracking-[0.2em] text-xs px-8 py-5 rounded-full hover:bg-[#C67D57] hover:text-[#F2EFEB] transition-all duration-300 disabled:opacity-50 ${light ? "bg-[#2F4A26] text-[#F2EFEB]" : "bg-[#F2EFEB] text-[#0D0C0B]"}`}
      >
        {loading ? t("form.sending") : t("form.send")}
        <ArrowRight strokeWidth={1.5} size={16} className="group-hover:translate-x-1 transition-transform" />
      </button>
    </form>
  );
};
